import { AwsRum, AwsRumConfig } from "aws-rum-web";
import {
  STAGE_CONFIGS,
  StageRUMConfig,
  getStageFromHostname,
} from "../config/stageConfig";

// https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/CloudWatch-RUM-custom-events.html
export interface CustomAwsRumEvent {
  activeUrl?: string;
  capturedError?: unknown;
  capturedErrorStatus?: number;
  apiUrl?: string;
  user?: string;
  component?: string;
  clickData?: {
    elementType?: string;
    elementId?: string;
    elementClass?: string;
    elementText?: string;
    coordinates?: { x: number; y: number };
    pageId?: string;
  };
  metadata?: { [name: string]: unknown };
}

export let awsRum: AwsRum | null = null;

export const APPLICATION_VERSION = "1.0.0";
export const APPLICATION_REGION = "us-east-1";

function getRUMConfig(hostname: string): StageRUMConfig | null {
  const stage = getStageFromHostname(hostname);
  return stage ? STAGE_CONFIGS[stage].rum : null;
}

export function startAWSRealUserMonitoring(hostname: string): void {
  if (awsRum) {
    return;
  }

  const rumConfig: StageRUMConfig | null = getRUMConfig(hostname);

  if (rumConfig === null) {
    return;
  }

  try {
    const config: AwsRumConfig = {
      sessionSampleRate: 1,
      guestRoleArn: rumConfig.unauthenticatedRoleArn,
      identityPoolId: rumConfig.identityPoolId,
      endpoint: rumConfig.endpoint,
      telemetries: ["errors", "performance", "http"],
      allowCookies: true,
      enableXRay: true,
      disableAutoPageView: false,
    };

    awsRum = new AwsRum(
      rumConfig.applicationId,
      APPLICATION_VERSION,
      APPLICATION_REGION,
      config,
    );
    initializeClickTracking();
  } catch (_error) {
    awsRum = null;
  }
}

function initializeClickTracking(): void {
  if (!awsRum) return;

  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;

    const clickEvent: CustomAwsRumEvent = {
      activeUrl: window.location.href,
      clickData: {
        elementType: target.tagName.toLowerCase(),
        elementId: target.id || undefined,
        elementClass: target.className || undefined,
        elementText: target.textContent?.substring(0, 100) || undefined,
        coordinates: { x: event.clientX, y: event.clientY },
        pageId: getPageId(),
      },
      metadata: {
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
      },
    };

    awsRumService.logCustomEvent("click_tracking", clickEvent);
  });
}

function getPageId(): string {
  if (window.location.pathname === "/") {
    const tab = new URL(window.location.href).searchParams.get("tab");
    return `home${tab ? `-${tab}` : ""}`;
  }
  return window.location.pathname;
}

/**
 * Get the current RUM instance
 */
export function getRumInstance(): AwsRum | null {
  return awsRum;
}

export const awsRumService = {
  logError: (eventType: string, customEvent: CustomAwsRumEvent): void => {
    if (awsRum) {
      awsRum.recordEvent(eventType, { ...customEvent });
    }
  },

  logCustomEvent: (eventType: string, customEvent: CustomAwsRumEvent): void => {
    if (awsRum) {
      awsRum.recordEvent(eventType, { ...customEvent });
    }
  },

  recordPageView: (pageId?: string): void => {
    if (awsRum) {
      awsRum.recordPageView(pageId || getPageId());
    }
  },
};

/**
 * Function that builds a custom AWS RUM event at the component level
 */
export function buildComponentLevelRumEvent(
  component: string,
  user: string,
  metadata?: { [name: string]: unknown },
): CustomAwsRumEvent {
  const customEvent: CustomAwsRumEvent = {
    user: user,
    component: component,
    activeUrl: window.location.href,
  };

  if (metadata) {
    customEvent.metadata = metadata;
  }

  return customEvent;
}
